import { timelineCreateWorkflow, USAGE_LIMIT_ERROR } from "@Api/Workflows/timelineCreate";
import { Dock, PageScroll } from "@Components/Page";
import { WorkflowWrapper } from "@Components/Workflows/Components/WorkflowWrapper";
import { useUserContext } from "@Hooks/contexts/useUserContext";
import { styled } from "@mui/material/styles";
import { Banner, Button, CheckIcon, Lede, PageTitle, ProgressBar } from "@UI";
import { getSectionText } from "@Utils/doc/textGetters";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
const RunDock = styled(Dock)({
    gap: "var(--j-sp-3)"
});
const Progress = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "var(--j-sp-1-5)"
});
export function TimelineCreate({ onExit }) {
    const { t } = useTranslation();
    const token = useUserContext((s)=>s?.user?.meta?.access_token.token);
    const [isLoading, setIsLoading] = React.useState(false);
    const [result, setResult] = React.useState(null);
    const [progress, setProgress] = React.useState({
        read: 0,
        total: 0
    });
    const abortRef = React.useRef(null);
    const onRunWorkflow = useCallback(async ()=>{
        if (!token) return;
        const controller = new AbortController();
        abortRef.current = controller;
        setIsLoading(true);
        setResult(null);
        setProgress({
            read: 0,
            total: 0
        });
        try {
            const sections = (await getSectionText()).map((part)=>part.section);
            if (controller.signal.aborted) return;
            setProgress({
                read: 0,
                total: sections.length
            });
            await timelineCreateWorkflow({
                token: token,
                sections,
                signal: controller.signal,
                onProgress: (read)=>setProgress({
                        read,
                        total: sections.length
                    })
            });
            if (!controller.signal.aborted) setResult("done");
        } catch (error) {
            if (controller.signal.aborted) return;
            const isUsageLimit = error instanceof Error && error.message === USAGE_LIMIT_ERROR;
            if (!isUsageLimit) console.error("Timeline creation failed:", error);
            setResult(isUsageLimit ? "usageLimit" : "error");
        } finally{
            if (abortRef.current === controller) setIsLoading(false);
        }
    }, [
        token
    ]);
    const onCancel = useCallback(()=>{
        abortRef.current?.abort();
        abortRef.current = null;
        setIsLoading(false);
    }, []);
    const isReading = progress.read < progress.total;
    return /*#__PURE__*/ React.createElement(WorkflowWrapper, {
        onBack: onExit
    }, /*#__PURE__*/ React.createElement(PageScroll, null, /*#__PURE__*/ React.createElement(PageTitle, {
        title: t("workflows.TimelineCreate.title"),
        lede: t("workflows.TimelineCreate.lede")
    })), /*#__PURE__*/ React.createElement(RunDock, null, result === "error" && /*#__PURE__*/ React.createElement(Banner, {
        tone: "error"
    }, t("workflows.TimelineCreate.failed")), result === "usageLimit" && /*#__PURE__*/ React.createElement(Banner, {
        tone: "warn"
    }, t("chat.usage_limit.title")), result === "done" && /*#__PURE__*/ React.createElement(Banner, {
        icon: /*#__PURE__*/ React.createElement(CheckIcon, null)
    }, t("workflows.TimelineCreate.done")), isLoading ? /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Progress, null, /*#__PURE__*/ React.createElement(Lede, null, isReading ? t("workflows.TimelineCreate.progress", {
        current: progress.read + 1,
        total: progress.total
    }) : t("workflows.TimelineCreate.running")), /*#__PURE__*/ React.createElement(ProgressBar, {
        value: isReading && progress.total ? progress.read / progress.total * 100 : undefined,
        label: t("workflows.TimelineCreate.title")
    })), /*#__PURE__*/ React.createElement(Button, {
        size: "lg",
        variant: "outline",
        onClick: onCancel
    }, t("generic.cancel"))) : /*#__PURE__*/ React.createElement(Button, {
        size: "lg",
        onClick: onRunWorkflow
    }, t("workflows.TimelineCreate.run"))));
}
